import { z } from 'zod';

export const AlertSeveritySchema = z.enum(['info', 'warning', 'critical']);
export type AlertSeverity = z.infer<typeof AlertSeveritySchema>;

export const AlertKindSchema = z.enum([
  /** Process left `online` for a failure state. */
  'crash',
  /** Restart counter climbed repeatedly inside the rule window. */
  'restart_loop',
  'cpu_high',
  'memory_high',
  /** pm2 daemon connection dropped. */
  'daemon_unreachable',
]);
export type AlertKind = z.infer<typeof AlertKindSchema>;

/**
 * A user-configurable threshold. `durationSeconds` prevents a single noisy
 * sample from firing an alert — the condition must hold for the whole window.
 */
export const AlertRuleSchema = z.object({
  id: z.string(),
  kind: AlertKindSchema,
  enabled: z.boolean(),
  /** Percent for cpu_high, bytes for memory_high, restart count for restart_loop. */
  threshold: z.number().nullable(),
  durationSeconds: z.number(),
  /** Empty means the rule applies to every process. */
  processNames: z.array(z.string()),
});
export type AlertRule = z.infer<typeof AlertRuleSchema>;

export const AlertSchema = z.object({
  id: z.string(),
  ruleId: z.string(),
  kind: AlertKindSchema,
  severity: AlertSeveritySchema,
  title: z.string(),
  detail: z.string(),
  processId: z.number().nullable(),
  processName: z.string().nullable(),
  triggeredAt: z.number(),
  acknowledgedAt: z.number().nullable(),
});
export type Alert = z.infer<typeof AlertSchema>;

export const DEFAULT_ALERT_RULES: AlertRule[] = [
  {
    id: 'crash',
    kind: 'crash',
    enabled: true,
    threshold: null,
    durationSeconds: 0,
    processNames: [],
  },
  {
    id: 'restart_loop',
    kind: 'restart_loop',
    enabled: true,
    threshold: 3,
    durationSeconds: 300,
    processNames: [],
  },
  {
    id: 'cpu_high',
    kind: 'cpu_high',
    enabled: true,
    threshold: 80,
    durationSeconds: 60,
    processNames: [],
  },
  {
    id: 'memory_high',
    kind: 'memory_high',
    enabled: false,
    // 400 MiB — below the 500 MiB max_memory_restart already set on tfd-web,
    // so this warns before pm2 force-restarts the process.
    threshold: 400 * 1024 * 1024,
    durationSeconds: 60,
    processNames: [],
  },
];
